/**
 * Environnement d'administration.
 *
 * Réservé aux administrateurs de plateforme. Chaque écran interroge la
 * plateforme entière, pas un compte : c'est ce qui distingue réellement cet
 * environnement des deux autres, et le serveur refuse ces routes à quiconque
 * n'est pas `is_platform_admin`.
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  api,
  telecharger,
  type CompteAdmin,
  type EntreeAudit,
  type Facture,
  type FactureDetaillee,
  type Retention,
  type Expediteur,
  type ExpediteurAValider,
  type GrilleTarifaire,
  type TarifCompte,
  type Registre,
  type Supervision as DonneesSupervision,
} from '../../api/client';
import {
  Bandeau,
  Carte,
  Champ,
  Chargement,
  EtatVide,
  Etiquette,
  Grille,
  MessageErreur,
  MessageSucces,
  PuceLibre,
  Statistique,
} from '../../components/Ui';
import { useEstProduction } from '../../lib/plateforme';
import { FactureImprimable } from '../client/Autres';
import { formaterDate, formaterDateCivile, formaterFcfa, formaterNombre } from '../../lib/format';

// --- Supervision du routage -------------------------------------------------

export function Supervision() {
  const [donnees, setDonnees] = useState<DonneesSupervision | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);

  useEffect(() => {
    const charger = () => api.admin.supervision().then(setDonnees).catch(setErreur);
    void charger();
    const minuterie = setInterval(charger, 5_000);
    return () => clearInterval(minuterie);
  }, []);

  if (erreur) return <MessageErreur erreur={erreur} />;
  if (!donnees) return <Chargement quoi="Chargement de la supervision…" />;

  const { gateway, stats } = donnees;

  return (
    <>
      {gateway.drivers.every((p) => !p.reaches_operator) && (
        <Bandeau titre="Pilote simulé — aucun trafic opérateur réel.">
          La passerelle configurée est le simulateur. Les chiffres ci-dessous sont réels, mais aucun
          message n’atteint un réseau GSM. Bascule par{' '}
          <span className="mono">GATEWAY_DRIVER=operateurs</span>.
        </Bandeau>
      )}

      {donnees.alerts.length > 0 && (
        <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {donnees.alerts.map((a, i) => (
            <div
              key={i}
              className={`bandeau ${a.niveau === 'critique' ? 'rouge' : 'orange'}`}
              style={{ margin: 0 }}
            >
              <strong style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }}>
                {a.niveau}
              </strong>{' '}
              — {a.message}
            </div>
          ))}
        </div>
      )}

      <div className="carte-relief">
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 12,
            marginBottom: 18,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,.6)',
            }}
          >
            Plateforme · dernières 24 heures
          </div>
          <div
            style={{
              marginLeft: 'auto',
              fontSize: 11.5,
              color: 'rgba(255,255,255,.7)',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
            }}
          >
            <span className="point-direct" />
            actualisé toutes les 5 s
          </div>
        </div>

        <div className="grille auto-150">
          <Statistique
            intitule="Messages émis"
            valeur={formaterNombre(stats.messages_24h)}
            note="production"
          />
          <Statistique
            intitule="Remis"
            valeur={formaterNombre(stats.delivered_24h)}
            note={
              stats.delivery_rate_per_mille !== null
                ? `${(stats.delivery_rate_per_mille / 10).toLocaleString('fr-FR')} %`
                : '—'
            }
          />
          <Statistique
            intitule="Non remis"
            valeur={<span style={{ color: 'var(--orange)' }}>{stats.undelivered_24h}</span>}
            note="recrédités"
          />
          <Statistique
            intitule="En vol"
            valeur={formaterNombre(stats.in_flight)}
            note="sans accusé final"
          />
          <Statistique
            intitule="Chiffre d’affaires"
            valeur={formaterNombre(stats.revenue_24h_xaf)}
            note="FCFA hors taxes"
          />
        </div>

        <div
          style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,.14)' }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11.5,
              color: 'rgba(255,255,255,.7)',
              marginBottom: 8,
              gap: 12,
            }}
          >
            <span>Débit accordé par contrat opérateur</span>
            <span className="mono">{gateway.global_tps_cap} SMS par seconde</span>
          </div>
          <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.55)' }}>
            Le facteur limitant est le contrat, jamais l’infrastructure. Un dépassement fait
            patienter les messages en file — il n’en perd aucun.
          </div>
        </div>
      </div>

      <div className="rangee" style={{ marginTop: 16 }}>
        <div className="large">
          <Carte titre="Files d’exécution" sansCorps>
            <div className="tableau-conteneur">
              <table style={{ minWidth: 480 }}>
                <thead>
                  <tr>
                    <th>File</th>
                    <th className="droite">En attente</th>
                    <th className="droite">Actives</th>
                    <th className="droite">Différées</th>
                    <th className="droite">En échec</th>
                  </tr>
                </thead>
                <tbody>
                  {donnees.queues.map((f) => (
                    <tr key={f.nom}>
                      <td className="mono">{f.nom}</td>
                      <td className="nombre">{f.waiting}</td>
                      <td className="nombre">{f.active}</td>
                      <td className="nombre">{f.delayed}</td>
                      <td
                        className="nombre"
                        style={{ color: f.failed > 0 ? 'var(--rouge)' : undefined }}
                      >
                        {f.failed}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="corps" style={{ paddingTop: 12 }}>
              <div className="aide" style={{ marginTop: 0 }}>
                <span className="mono">otp</span> est prioritaire :{' '}
                <span className="mono">campagnes</span> ne doit jamais retarder un code de
                validation attendu par un client devant son écran.
              </div>
            </div>
          </Carte>
        </div>

        <div className="etroit">
          <Carte titre="État de la plateforme">
            <dl className="definitions">
              {gateway.drivers.map((pilote) => (
                <Fragment key={pilote.operator}>
                  <dt>Passerelle {pilote.operator}</dt>
                  <dd>
                    <PuceLibre
                      ton={pilote.available ? 'succes' : pilote.configured ? 'echec' : 'attente'}
                    >
                      {pilote.available
                        ? 'Disponible'
                        : pilote.configured
                          ? 'Injoignable'
                          : 'À configurer'}
                    </PuceLibre>
                    {!pilote.reaches_operator && <span className="aide"> — simulateur</span>}
                  </dd>
                </Fragment>
              ))}
              <dt>Comptes actifs</dt>
              <dd>{stats.active_accounts}</dd>
              <dt>Comptes à solde bas</dt>
              <dd style={{ color: stats.low_balance_accounts > 0 ? 'var(--rouge)' : undefined }}>
                {stats.low_balance_accounts}
              </dd>
              <dt>Expéditeurs en attente</dt>
              <dd>{stats.pending_sender_ids}</dd>
              <dt>Retraits STOP</dt>
              <dd>{formaterNombre(stats.opt_outs_total)}</dd>
              <dt>Administrateurs</dt>
              <dd>{stats.platform_admins}</dd>
            </dl>
            <div className="aide">
              La passerelle n’entre pas dans la disponibilité de l’API : un bind rompu ne perd aucun
              message, les envois patientent. Elle est signalée, pas bloquante.
            </div>
          </Carte>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <Carte titre="Acheminement par opérateur" sansCorps>
          <EtatVide titre="Le détail par opérateur nécessite des passerelles raccordées.">
            <div className="aide" style={{ marginTop: 0 }}>
              Volume, taux de remise et latence par réseau s’afficheront ici dès que{' '}
              <span className="mono">GATEWAY_DRIVER=operateurs</span> pointera vers les API Airtel
              et Moov. Le pilote retenu découle du préfixe du destinataire.
            </div>
          </EtatVide>
        </Carte>
      </div>
    </>
  );
}

// --- Comptes clients --------------------------------------------------------

export function Comptes() {
  const [comptes, setComptes] = useState<CompteAdmin[] | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [ouverture, setOuverture] = useState(false);
  /** Compte dont le panneau de gestion est déplié. Un seul à la fois. */
  const [gere, setGere] = useState<string | null>(null);
  const estProduction = useEstProduction();

  const charger = useCallback(() => {
    api.admin
      .comptes()
      .then(({ data }) => setComptes(data))
      .catch(setErreur);
  }, []);

  useEffect(charger, [charger]);

  if (erreur && !comptes) return <MessageErreur erreur={erreur} />;
  if (!comptes) return <Chargement quoi="Chargement des comptes…" />;

  return (
    <>
      <Carte
        titre="Ouvrir un compte client"
        action={
          <button className="secondaire" onClick={() => setOuverture((v) => !v)}>
            {ouverture ? 'Fermer' : 'Ouvrir un compte'}
          </button>
        }
      >
        <div className="aide" style={{ marginTop: 0 }}>
          En production, le client ne s’inscrit pas lui-même : c’est vous qui ouvrez son compte,
          après intégration réussie sur la plateforme de test. Le formulaire reprend celui de
          l’inscription en test, augmenté des mentions légales —{' '}
          <strong>et la personne créée ici est le propriétaire du compte</strong> : c’est elle qui
          invitera ensuite son équipe depuis son propre espace.
        </div>

        {!estProduction && (
          <Bandeau ton="orange">
            Cette plateforme est celle de <strong>test</strong> : les clients y ouvrent leur compte
            eux-mêmes. L’écran fonctionne, mais aucun compte créé ici ne quittera le bac à sable.
          </Bandeau>
        )}

        {succes && <MessageSucces>{succes}</MessageSucces>}

        {ouverture && (
          <FormulaireOuvertureCompte
            onOuvert={(message) => {
              setOuverture(false);
              setSucces(message);
              charger();
            }}
          />
        )}
      </Carte>

      <Carte sansCorps>
        <div className="tableau-conteneur">
          <table style={{ minWidth: 1020 }}>
            <thead>
              <tr>
                <th>Compte</th>
                <th>Type</th>
                <th className="droite">Solde</th>
                <th className="droite">Débit</th>
                <th>Expéditeurs</th>
                <th className="droite">Clés</th>
                <th className="droite">Aujourd’hui</th>
                <th>État</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {comptes.map((c) => (
                <Fragment key={c.id}>
                  <tr>
                    <td>
                      <div style={{ fontWeight: 500 }}>{c.name}</div>
                      <div style={{ color: 'var(--muet)', fontSize: 12 }}>
                        {c.nif ?? 'NIF non renseigné'}
                      </div>
                    </td>
                    <td>
                      {c.sandbox_only ? (
                        <PuceLibre ton="attente">Intégration</PuceLibre>
                      ) : (
                        <PuceLibre ton="ok">Production</PuceLibre>
                      )}
                    </td>
                    <td
                      className="nombre"
                      style={{ color: c.low_balance ? 'var(--rouge)' : 'var(--vert)' }}
                    >
                      {formaterFcfa(c.balance_xaf)}
                    </td>
                    <td className="nombre">{c.tps_limit}/s</td>
                    <td>
                      <CelluleExpediteurs compte={c} />
                    </td>
                    <td className="nombre">{c.active_api_keys}</td>
                    <td className="nombre">{formaterNombre(c.messages_today)}</td>
                    <td>
                      {c.status !== 'active' ? (
                        <PuceLibre ton="echec">Suspendu</PuceLibre>
                      ) : c.low_balance ? (
                        <PuceLibre ton="attente">Solde bas</PuceLibre>
                      ) : (
                        <PuceLibre ton="succes">Actif</PuceLibre>
                      )}
                    </td>
                    <td className="droite">
                      <button
                        className="secondaire petit"
                        onClick={() => setGere((v) => (v === c.id ? null : c.id))}
                        aria-expanded={gere === c.id}
                      >
                        {gere === c.id ? 'Fermer' : 'Gérer'}
                      </button>
                    </td>
                  </tr>
                  {gere === c.id && (
                    <tr>
                      <td colSpan={9} style={{ background: 'var(--fond)' }}>
                        <PanneauCompte compte={c} onChange={charger} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="corps" style={{ paddingTop: 12 }}>
          <div className="aide" style={{ marginTop: 0 }}>
            Un compte <strong>Intégration</strong> ne peut ni détenir de clé de production, ni
            émettre en production, ni être débité — la base elle-même le refuse. Les recharges se
            font depuis l’écran Facturation ; l’équipe d’un compte se consulte depuis l’écran
            Utilisateurs, mais c’est le client qui la compose.
          </div>
        </div>
      </Carte>
    </>
  );
}

/**
 * Noms d'expéditeur d'un compte, tels qu'ils partiront chez l'opérateur.
 *
 * Cette colonne portait un compteur. Il disait qu'un client avait trois
 * expéditeurs sans jamais dire lesquels, et le nom ne se lisait nulle part
 * ailleurs : l'écran des demandes ne montre que ce qui est en attente, donc
 * valider un nom le faisait disparaître de l'administration.
 *
 * Au-delà de trois noms on s'arrête et on compte le reste — une ligne de
 * tableau n'est pas une liste. Le titre de la cellule les porte tous, pour
 * qu'aucun ne soit hors d'atteinte.
 */
function CelluleExpediteurs({ compte }: { compte: CompteAdmin }) {
  const { sender_ids: noms, pending_sender_ids: attente } = compte;
  const VISIBLES = 3;

  return (
    <div title={noms.length > 0 ? noms.join(' · ') : undefined}>
      {noms.length === 0 ? (
        <span style={{ color: 'var(--muet)' }}>Aucun</span>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
          {noms.slice(0, VISIBLES).map((nom) => (
            <Etiquette key={nom}>{nom}</Etiquette>
          ))}
          {noms.length > VISIBLES && (
            <span style={{ color: 'var(--muet)', fontSize: 12 }}>+{noms.length - VISIBLES}</span>
          )}
        </div>
      )}
      {attente > 0 && (
        <div style={{ color: 'var(--muet)', fontSize: 12, marginTop: 4 }}>{attente} en attente</div>
      )}
    </div>
  );
}

/**
 * Gestes d'administration sur un compte : son tarif, ses expéditeurs.
 *
 * Replié, et chargé seulement à l'ouverture. Ces deux lectures coûtent une
 * requête chacune — les verser dans la liste des comptes la rendrait
 * proportionnelle au nombre de clients pour un panneau qu'on ouvre une fois
 * sur trente.
 */
function PanneauCompte({ compte, onChange }: { compte: CompteAdmin; onChange: () => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: '8px 0' }}>
      <div style={{ flex: '1 1 340px', minWidth: 0 }}>
        <BlocTarif compte={compte} />
      </div>
      <div style={{ flex: '1 1 340px', minWidth: 0 }}>
        <BlocExpediteurs compte={compte} onChange={onChange} />
      </div>
    </div>
  );
}

/** D'où vient le prix qu'un compte paie. Le serveur le dit, on ne le devine pas. */
const ORIGINE_TARIF: Record<TarifCompte['price_origin'], string> = {
  palier_compte: 'palier négocié pour ce compte',
  grille_defaut: 'grille commune de la plateforme',
  configuration: 'prix par défaut, aucun palier ne s’applique',
};

/** Un entier positif écrit en clair — ce que la saisie d'un montant XAF autorise. */
const entierSaisi = (valeur: string) => /^\d+$/.test(valeur.trim());

/**
 * Tarif d'un compte.
 *
 * Le prix appliqué vient du serveur et n'est jamais recalculé ici : le volume
 * du mois entre dans la résolution du palier, et un prix recomposé par la
 * console finirait par différer de celui qui est facturé.
 *
 * Le seuil est modifiable mais vaut zéro par défaut, car c'est le geste
 * courant — « ce client paie tant ». Les paliers supérieurs servent aux
 * remises négociées par volume.
 */
function BlocTarif({ compte }: { compte: CompteAdmin }) {
  const [tarif, setTarif] = useState<TarifCompte | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [prix, setPrix] = useState('');
  const [seuil, setSeuil] = useState('0');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    api.admin.tarifCompte(compte.id).then(setTarif).catch(setErreur);
  }, [compte.id]);

  /** Les trois appels rendent l'état complet : rien à recharger, rien à recomposer. */
  async function appliquer(geste: () => Promise<TarifCompte>) {
    setErreur(null);
    setEnCours(true);
    try {
      setTarif(await geste());
      setPrix('');
      setSeuil('0');
    } catch (e) {
      setErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  if (!tarif) {
    return (
      <Carte titre="Tarif">
        {erreur ? <MessageErreur erreur={erreur} /> : <Chargement quoi="Chargement du tarif…" />}
      </Carte>
    );
  }

  const valide = entierSaisi(prix) && entierSaisi(seuil);

  return (
    <Carte titre="Tarif">
      <MessageErreur erreur={erreur} />

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 22, fontWeight: 500 }} className="mono">
          {formaterFcfa(tarif.effective_price_per_segment_xaf)}
          <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--muet)' }}> / segment</span>
        </div>
        <div style={{ color: 'var(--muet)', fontSize: 12 }}>
          {ORIGINE_TARIF[tarif.price_origin]} · {formaterNombre(tarif.segments_this_month)} segments
          ce mois
        </div>
      </div>

      {tarif.data.length === 0 ? (
        <div className="aide" style={{ marginTop: 0 }}>
          Aucun palier négocié : ce compte suit la grille commune, et la suivra si elle change. Le
          prix par défaut de la plateforme est de{' '}
          {formaterFcfa(tarif.default_price_per_segment_xaf)} par segment.
        </div>
      ) : (
        <table style={{ marginBottom: 12 }}>
          <thead>
            <tr>
              <th className="droite">À partir de</th>
              <th className="droite">Prix</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tarif.data.map((p) => (
              <tr key={p.id}>
                <td className="nombre droite">
                  {p.min_segments_month === 0
                    ? '1er segment'
                    : `${formaterNombre(p.min_segments_month)} / mois`}
                </td>
                <td className="nombre droite">{formaterFcfa(p.price_per_segment_xaf)}</td>
                <td className="droite">
                  <button
                    className="secondaire petit"
                    disabled={enCours}
                    onClick={() => appliquer(() => api.admin.retirerPalier(compte.id, p.id))}
                  >
                    Retirer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 130px' }}>
          <Champ libelle="Prix du segment" valeur={prix} onChange={setPrix} type="number" />
        </div>
        <div style={{ flex: '1 1 130px' }}>
          <Champ
            libelle="À partir de (segments / mois)"
            valeur={seuil}
            onChange={setSeuil}
            type="number"
          />
        </div>
        <button
          className="primaire"
          disabled={!valide || enCours}
          onClick={() =>
            appliquer(() =>
              api.admin.definirTarif(compte.id, {
                min_segments_month: Number(seuil),
                price_per_segment_xaf: Number(prix),
              }),
            )
          }
        >
          Fixer
        </button>
      </div>

      <div className="aide">
        En francs CFA entiers — le XAF n’a pas de sous-unité. Fixer un prix à un seuil déjà défini
        le remplace. Le changement ne vaut que pour les envois à venir : le prix est figé dans
        chaque message à l’acceptation, donc aucune facture déjà calculée ne bouge.
      </div>
    </Carte>
  );
}

/** Aspect de la pastille d'un expéditeur. Ni vert ni rouge : ce n'est pas un montant. */
const ASPECT_EXPEDITEUR: Record<
  Expediteur['status'],
  { ton: 'ok' | 'attente' | 'neutre'; libelle: string }
> = {
  approved: { ton: 'ok', libelle: 'Validé' },
  pending: { ton: 'attente', libelle: 'En attente' },
  rejected: { ton: 'neutre', libelle: 'Refusé' },
};

/**
 * Expéditeurs d'un compte, et leur suppression.
 *
 * Supprimer n'est pas refuser. Un refus garde la ligne, donc garde le nom pris
 * pour ce compte et interdit au client de le redemander ; la suppression le
 * libère, pour ce compte comme pour la plateforme. C'est ce qu'il faut quand un
 * client abandonne une marque, quand un nom a été saisi de travers, ou quand un
 * expéditeur doit repartir chez un autre client.
 *
 * La confirmation se fait sur place, en deux clics sur le même bouton. Une
 * boîte de dialogue du navigateur se clique sans la lire, et son texte ne peut
 * pas porter le nom qu'on est en train de perdre.
 */
function BlocExpediteurs({ compte, onChange }: { compte: CompteAdmin; onChange: () => void }) {
  const [expediteurs, setExpediteurs] = useState<Expediteur[] | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [aConfirmer, setAConfirmer] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(() => {
    api.admin
      .expediteursDuCompte(compte.id)
      .then(({ data }) => setExpediteurs(data))
      .catch(setErreur);
  }, [compte.id]);

  useEffect(charger, [charger]);

  async function supprimer(id: string) {
    setErreur(null);
    setEnCours(true);
    try {
      await api.admin.supprimerExpediteur(id);
      setAConfirmer(null);
      charger();
      // La colonne « Expéditeurs » de la liste vient d'une autre requête : sans
      // cela, elle continuerait d'afficher le nom supprimé.
      onChange();
    } catch (e) {
      setErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  if (!expediteurs) {
    return (
      <Carte titre="Expéditeurs">
        {erreur ? (
          <MessageErreur erreur={erreur} />
        ) : (
          <Chargement quoi="Chargement des expéditeurs…" />
        )}
      </Carte>
    );
  }

  return (
    <Carte titre="Expéditeurs">
      <MessageErreur erreur={erreur} />

      {expediteurs.length === 0 ? (
        <div className="aide" style={{ marginTop: 0 }}>
          Ce compte n’a demandé aucun expéditeur. Il ne peut donc rien émettre : l’opérateur refuse
          un message dont le nom d’émetteur n’est pas déclaré.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {expediteurs.map((e) => (
            <div
              key={e.id}
              style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Etiquette>{e.value}</Etiquette>
              <PuceLibre ton={ASPECT_EXPEDITEUR[e.status].ton}>
                {ASPECT_EXPEDITEUR[e.status].libelle}
              </PuceLibre>
              <span style={{ color: 'var(--muet)', fontSize: 12 }}>
                {formaterDate(e.created_at)}
              </span>
              <span style={{ marginLeft: 'auto' }}>
                {aConfirmer === e.id ? (
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      className="secondaire petit"
                      disabled={enCours}
                      onClick={() => supprimer(e.id)}
                    >
                      Supprimer « {e.value} »
                    </button>
                    <button
                      className="lien"
                      style={{ fontSize: 12 }}
                      onClick={() => setAConfirmer(null)}
                    >
                      Annuler
                    </button>
                  </span>
                ) : (
                  <button className="secondaire petit" onClick={() => setAConfirmer(e.id)}>
                    Supprimer
                  </button>
                )}
              </span>
              {e.reason && (
                <div style={{ flexBasis: '100%', color: 'var(--muet)', fontSize: 12 }}>
                  {e.reason}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="aide">
        La suppression est définitive et libère le nom : ce client pourra le redemander, et un autre
        pourra l’obtenir. Les messages déjà émis gardent le leur — l’historique et les factures ne
        bougent pas. Pour refuser une demande en gardant le nom réservé, passez par l’écran{' '}
        <strong>Expéditeurs</strong>.
      </div>
    </Carte>
  );
}

// --- Expéditeurs à valider --------------------------------------------------

export function ExpediteursAValider() {
  const [attente, setAttente] = useState<ExpediteurAValider[] | null>(null);
  const [motifs, setMotifs] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState<unknown>(null);

  const recharger = () =>
    api.admin
      .expediteurs()
      .then(({ data }) => setAttente(data))
      .catch(setErreur);

  useEffect(() => {
    void recharger();
  }, []);

  async function statuer(id: string, decision: 'approve' | 'reject') {
    setErreur(null);
    try {
      await api.admin.statuerExpediteur(id, decision, motifs[id]);
      await recharger();
    } catch (e) {
      setErreur(e);
    }
  }

  if (erreur && !attente) return <MessageErreur erreur={erreur} />;
  if (!attente) return <Chargement quoi="Chargement des demandes…" />;

  return (
    <>
      <MessageErreur erreur={erreur} />

      <Bandeau titre="Un expéditeur validé est unique sur toute la plateforme.">
        Deux clients ne peuvent pas émettre sous le même nom : c’est une contrainte de l’opérateur,
        et c’est aussi ce qui permet de rattacher une réponse STOP au bon compte. Un refus doit être
        motivé — le motif est communiqué au client tel quel.
      </Bandeau>

      {attente.length === 0 ? (
        <Carte sansCorps>
          <EtatVide titre="Aucune demande en attente." />
        </Carte>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {attente.map((e) => (
            <Carte key={e.id}>
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span className="etiquette-sid" style={{ fontSize: 13 }}>
                      {e.value}
                    </span>
                    <span
                      className="mono"
                      style={{
                        fontSize: 12,
                        color: e.length > 11 ? 'var(--rouge)' : 'var(--muet)',
                      }}
                    >
                      {e.length} / 11 caractères
                    </span>
                  </div>
                  <div style={{ marginTop: 8, color: 'var(--muet)', fontSize: 13 }}>
                    Demandé par <strong>{e.account_name}</strong> · {formaterDate(e.created_at)}
                  </div>
                  {e.anomaly && (
                    <div
                      className="bandeau rouge"
                      style={{ marginTop: 12, marginBottom: 0, fontSize: 13 }}
                    >
                      {e.anomaly}
                    </div>
                  )}
                </div>

                <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                  <label className="champ">
                    <span>Motif — obligatoire en cas de refus</span>
                    <input
                      type="text"
                      value={motifs[e.id] ?? ''}
                      onChange={(ev) => setMotifs({ ...motifs, [e.id]: ev.target.value })}
                      placeholder="Nom de marque non déposé, collision avec un client existant…"
                    />
                  </label>
                  <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                    <button
                      className="primaire"
                      onClick={() => statuer(e.id, 'approve')}
                      disabled={e.length > 11}
                    >
                      Valider
                    </button>
                    <button
                      className="secondaire"
                      onClick={() => statuer(e.id, 'reject')}
                      disabled={!motifs[e.id]?.trim()}
                    >
                      Refuser
                    </button>
                  </div>
                </div>
              </div>
            </Carte>
          ))}
        </div>
      )}
    </>
  );
}

// --- Tarifs et marges -------------------------------------------------------

export function Tarifs() {
  const [grille, setGrille] = useState<GrilleTarifaire | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);

  useEffect(() => {
    api.admin.tarifs().then(setGrille).catch(setErreur);
  }, []);

  if (erreur) return <MessageErreur erreur={erreur} />;
  if (!grille) return <Chargement quoi="Chargement de la grille…" />;

  return (
    <>
      <Bandeau titre="La marge ne peut pas encore être calculée.">
        {grille.margin_note} Tant que ce chiffre est absent, afficher une marge serait inventer un
        résultat.
      </Bandeau>

      <div className="grille auto-210" style={{ marginBottom: 16 }}>
        <div className="carte">
          <div className="corps stat">
            <div className="intitule">Prix par défaut</div>
            <div className="valeur">{grille.default_price_per_segment_xaf}</div>
            <div className="note">FCFA par segment, si aucun palier ne s’applique</div>
          </div>
        </div>
        <div className="carte">
          <div className="corps stat">
            <div className="intitule">TVA</div>
            <div className="valeur">{grille.vat_rate_bps / 100} %</div>
            <div className="note">appliquée au total, jamais message par message</div>
          </div>
        </div>
        <div className="carte">
          <div className="corps stat">
            <div className="intitule">Prix de gros opérateur</div>
            <div className="valeur" style={{ color: 'var(--muet)' }}>
              —
            </div>
            <div className="note">contrat non signé</div>
          </div>
        </div>
        <div className="carte">
          <div className="corps stat">
            <div className="intitule">Paliers définis</div>
            <div className="valeur">{grille.data.length}</div>
            <div className="note">grille par défaut et grilles négociées</div>
          </div>
        </div>
      </div>

      <Carte titre="Paliers dégressifs" sansCorps>
        <div className="tableau-conteneur">
          <table style={{ minWidth: 560 }}>
            <thead>
              <tr>
                <th>Portée</th>
                <th className="droite">À partir de</th>
                <th className="droite">Prix du segment</th>
                <th className="droite">Marge</th>
              </tr>
            </thead>
            <tbody>
              {grille.data.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.account_id ? (
                      <PuceLibre ton="ok">{p.scope}</PuceLibre>
                    ) : (
                      <PuceLibre ton="neutre">{p.scope}</PuceLibre>
                    )}
                  </td>
                  <td className="nombre">
                    {p.min_segments_month === 0
                      ? 'dès le 1er segment'
                      : `${formaterNombre(p.min_segments_month)} segments / mois`}
                  </td>
                  <td className="nombre">{formaterFcfa(p.price_per_segment_xaf)}</td>
                  <td className="nombre" style={{ color: 'var(--muet)' }}>
                    —
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="corps" style={{ paddingTop: 12 }}>
          <div className="aide" style={{ marginTop: 0 }}>
            Un palier propre à un compte prime toujours sur la grille par défaut. Le prix est figé
            dans le message à l’acceptation : un changement de grille ne réécrit jamais
            l’historique, et une facture se recalcule à l’identique des mois plus tard.
          </div>
        </div>
      </Carte>
    </>
  );
}

// --- Registre de consentement -----------------------------------------------

export function RegistreConsentement() {
  const [registre, setRegistre] = useState<Registre | null>(null);
  const [enClair, setEnClair] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);

  useEffect(() => {
    api.admin.registre(enClair).then(setRegistre).catch(setErreur);
  }, [enClair]);

  if (erreur) return <MessageErreur erreur={erreur} />;
  if (!registre) return <Chargement quoi="Chargement du registre…" />;

  return (
    <>
      <Bandeau titre="Loi 025/2023 — le droit de retrait s’exerce sans frais et sans délai.">
        Un retrait vaut pour le compte émetteur uniquement, jamais pour la plateforme entière. La
        liste STOP est appliquée <strong>avant émission</strong> : un message filtré n’est pas
        facturé. Rétention : {registre.retention_months} mois.
      </Bandeau>

      <Carte
        titre={`Retraits enregistrés — ${registre.data.length} plus récents`}
        action={
          <button className="secondaire petit" onClick={() => setEnClair(!enClair)}>
            {enClair ? 'Masquer les numéros' : 'Révéler les numéros'}
          </button>
        }
        sansCorps
      >
        {registre.data.length === 0 ? (
          <EtatVide titre="Aucun retrait enregistré." />
        ) : (
          <div className="tableau-conteneur">
            <table style={{ minWidth: 620 }}>
              <thead>
                <tr>
                  <th>Horodatage</th>
                  <th>Numéro</th>
                  <th>Compte concerné</th>
                  <th>Origine</th>
                  <th>Mot-clé</th>
                </tr>
              </thead>
              <tbody>
                {registre.data.map((r) => (
                  <tr key={r.id}>
                    <td className="mono" style={{ fontSize: 12.5, color: 'var(--muet)' }}>
                      {formaterDate(r.created_at)}
                    </td>
                    <td className="mono" style={{ fontSize: 12.5 }}>
                      {r.to}
                    </td>
                    <td>{r.account_name}</td>
                    <td>
                      <PuceLibre ton={r.source === 'sms_stop' ? 'ok' : 'neutre'}>
                        {r.source === 'sms_stop'
                          ? 'Réponse STOP'
                          : r.source === 'api'
                            ? 'API'
                            : r.source === 'console'
                              ? 'Console'
                              : 'Import'}
                      </PuceLibre>
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {r.keyword ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="corps" style={{ paddingTop: 12 }}>
          <div className="aide" style={{ marginTop: 0 }}>
            Les numéros sont masqués par défaut : un registre ouvert en permanence est une base de
            données personnelles exposée. L’export horodaté et tracé pour contrôle ARCEP ou APDPVP
            arrive au lot 6.
          </div>
        </div>
      </Carte>
    </>
  );
}

// --- Facturation OHADA ------------------------------------------------------

const ASPECT: Record<
  Facture['status'],
  { libelle: string; ton: 'ok' | 'attente' | 'neutre' | 'succes' | 'echec' }
> = {
  draft: { libelle: 'brouillon', ton: 'neutre' },
  issued: { libelle: 'émise', ton: 'attente' },
  paid: { libelle: 'réglée', ton: 'succes' },
  cancelled: { libelle: 'annulée', ton: 'echec' },
};

/** Mois clos les plus récents : on ne facture jamais un mois en cours. */
function moisClos(nombre = 6): { annee: number; mois: number; libelle: string }[] {
  const liste: { annee: number; mois: number; libelle: string }[] = [];
  const maintenant = new Date();
  for (let i = 1; i <= nombre; i += 1) {
    const d = new Date(Date.UTC(maintenant.getUTCFullYear(), maintenant.getUTCMonth() - i, 15));
    liste.push({
      annee: d.getUTCFullYear(),
      mois: d.getUTCMonth() + 1,
      libelle: d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    });
  }
  return liste;
}

export function FacturationAdmin() {
  const exercice = new Date().getUTCFullYear();
  const periodes = moisClos();

  const [factures, setFactures] = useState<Facture[] | null>(null);
  const [trous, setTrous] = useState<number[]>([]);
  const [comptes, setComptes] = useState<CompteAdmin[]>([]);
  const [erreur, setErreur] = useState<unknown>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [ouverte, setOuverte] = useState<FactureDetaillee | null>(null);
  const [occupe, setOccupe] = useState(false);
  /**
   * Saisie demandée par une action sur une facture. Une invite `window.prompt`
   * bloquerait la page entière ; ici la référence ou le motif se saisit dans le
   * document, sous les yeux de qui décide.
   */
  const [saisie, setSaisie] = useState<{
    id: string;
    numero: string;
    quoi: 'pay' | 'cancel';
  } | null>(null);
  const [texteSaisie, setTexteSaisie] = useState('');

  // Génération
  const [compteChoisi, setCompteChoisi] = useState('');
  const [periodeChoisie, setPeriodeChoisie] = useState(0);

  // Recharge
  const [compteRecharge, setCompteRecharge] = useState('');
  const [montant, setMontant] = useState('');
  const [reference, setReference] = useState('');
  const [moyen, setMoyen] = useState('virement');

  /** Relit les données. Ne touche ni au succès ni à l'erreur : `agir` s'en charge. */
  async function rafraichir() {
    const [liste, comptesApi] = await Promise.all([
      api.admin.factures(exercice),
      api.admin.comptes(),
    ]);
    setFactures(liste.data);
    setTrous(liste.numbering_gaps);
    setComptes(comptesApi.data);
  }

  useEffect(() => {
    rafraichir().catch(setErreur);
  }, []);

  async function agir(action: () => Promise<void>) {
    setOccupe(true);
    setErreur(null);
    setSucces(null);
    try {
      await action();
      await rafraichir();
    } catch (e) {
      setErreur(e);
    } finally {
      setOccupe(false);
    }
  }

  function genererBrouillon() {
    const periode = periodes[periodeChoisie]!;
    return agir(async () => {
      const brouillon = await api.admin.brouillonFacture(compteChoisi, periode.annee, periode.mois);
      setOuverte(brouillon);
      setSucces(
        `Brouillon établi pour ${brouillon.account_name} — ${periode.libelle}. ` +
          (brouillon.missing_mentions && brouillon.missing_mentions.length > 0
            ? `Mentions manquantes : ${brouillon.missing_mentions.join(', ')}.`
            : 'Vérifiez les lignes avant d’émettre : l’émission consomme un numéro.'),
      );
    });
  }

  function crediter() {
    return agir(async () => {
      const resultat = await api.admin.recharger({
        account_id: compteRecharge,
        amount_xaf: Number(montant),
        payment_reference: reference.trim(),
        payment_method: moyen,
      });
      setSucces(
        resultat.duplicate
          ? `Référence ${reference.trim()} déjà enregistrée : aucun second crédit. Solde inchangé, ${formaterFcfa(resultat.balance_xaf)}.`
          : `${resultat.account_name} crédité. Nouveau solde : ${formaterFcfa(resultat.balance_xaf)}.`,
      );
      setMontant('');
      setReference('');
    });
  }

  if (erreur && !factures) return <MessageErreur erreur={erreur} />;
  if (!factures) return <Chargement quoi="Chargement de la facturation…" />;

  const emissionPossible = compteChoisi !== '';
  const rechargePossible =
    compteRecharge !== '' && Number(montant) > 0 && reference.trim().length >= 3;

  return (
    <>
      {trous.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Bandeau ton="rouge" titre="Trou dans la numérotation. ">
            Numéros manquants sur l’exercice {exercice} : {trous.join(', ')}. Une suite de factures
            doit être continue ; un trou se lit comme une facture soustraite. À corriger avant tout
            contrôle.
          </Bandeau>
        </div>
      )}

      {erreur ? <MessageErreur erreur={erreur} /> : null}
      {succes ? <MessageSucces>{succes}</MessageSucces> : null}

      <div className="rangee" style={{ marginTop: succes || erreur ? 16 : 0 }}>
        <div className="etroit">
          <Carte titre="Établir une facture">
            <label className="champ" style={{ marginBottom: 14 }}>
              <span>Compte</span>
              <select value={compteChoisi} onChange={(e) => setCompteChoisi(e.target.value)}>
                <option value="">Choisir un compte…</option>
                {comptes
                  .filter((c) => !c.sandbox_only)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </label>

            <label className="champ" style={{ marginBottom: 14 }}>
              <span>Période</span>
              <select
                value={periodeChoisie}
                onChange={(e) => setPeriodeChoisie(Number(e.target.value))}
              >
                {periodes.map((p, i) => (
                  <option key={`${p.annee}-${p.mois}`} value={i}>
                    {p.libelle}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              className="accent"
              disabled={!emissionPossible || occupe}
              onClick={genererBrouillon}
              style={{ marginTop: 12 }}
            >
              Établir le brouillon
            </button>

            <div className="aide">
              Seuls les mois clos sont proposés : un message émis hier peut encore être recrédité
              aujourd’hui, et la facture serait à corriger dès le lendemain. Le numéro n’est
              attribué qu’à l’émission — un brouillon abandonné ne laisse pas de trou.
            </div>
          </Carte>
        </div>

        <div className="etroit">
          <Carte titre="Créditer un compte">
            <label className="champ" style={{ marginBottom: 14 }}>
              <span>Compte</span>
              <select value={compteRecharge} onChange={(e) => setCompteRecharge(e.target.value)}>
                <option value="">Choisir un compte…</option>
                {comptes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} — {formaterFcfa(c.balance_xaf)}
                  </option>
                ))}
              </select>
            </label>

            <label className="champ" style={{ marginBottom: 14 }}>
              <span>Montant en FCFA</span>
              <input
                type="number"
                min={1}
                step={1}
                value={montant}
                onChange={(e) => setMontant(e.target.value)}
                placeholder="250000"
              />
            </label>

            <label className="champ" style={{ marginBottom: 14 }}>
              <span>Moyen de paiement</span>
              <select value={moyen} onChange={(e) => setMoyen(e.target.value)}>
                <option value="virement">Virement bancaire</option>
                <option value="mobile_money">Mobile money</option>
                <option value="especes">Espèces</option>
                <option value="compensation">Compensation</option>
              </select>
            </label>

            <label className="champ" style={{ marginBottom: 14 }}>
              <span>Référence du paiement</span>
              <input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="VIR-2026-08-0042"
              />
            </label>

            <button
              type="button"
              className="accent"
              disabled={!rechargePossible || occupe}
              onClick={crediter}
              style={{ marginTop: 12 }}
            >
              Créditer
            </button>

            <div className="aide">
              La référence porte l’idempotence : rejouer le même avis de crédit ne crédite pas deux
              fois, et l’écran vous le dira.
            </div>
          </Carte>
        </div>
      </div>

      {saisie && (
        <div style={{ marginTop: 16 }}>
          <Carte
            titre={
              saisie.quoi === 'pay'
                ? `Règlement de la facture ${saisie.numero}`
                : `Annulation de la facture ${saisie.numero}`
            }
          >
            <label className="champ" style={{ marginBottom: 14 }}>
              <span>
                {saisie.quoi === 'pay' ? 'Référence du règlement' : 'Motif de l’annulation'}
              </span>
              <input
                value={texteSaisie}
                onChange={(e) => setTexteSaisie(e.target.value)}
                placeholder={
                  saisie.quoi === 'pay'
                    ? 'VIR-2026-09-0117'
                    : 'Périmètre corrigé après accord du client'
                }
                autoFocus
              />
            </label>

            <div className="rangee-boutons">
              <button
                type="button"
                className="accent"
                disabled={occupe || texteSaisie.trim().length < 3}
                onClick={() =>
                  agir(async () => {
                    if (saisie.quoi === 'pay') {
                      await api.admin.reglerFacture(saisie.id, texteSaisie.trim());
                      setSucces(`${saisie.numero} marquée réglée.`);
                    } else {
                      const r = await api.admin.annulerFacture(saisie.id, texteSaisie.trim());
                      setSucces(
                        `${saisie.numero} annulée par l’avoir ${r.credit_note.number}. La facture garde son numéro : la suite reste sans trou.`,
                      );
                    }
                    setSaisie(null);
                    setTexteSaisie('');
                  })
                }
              >
                {saisie.quoi === 'pay' ? 'Confirmer le règlement' : 'Annuler la facture'}
              </button>
              <button
                type="button"
                className="secondaire"
                onClick={() => {
                  setSaisie(null);
                  setTexteSaisie('');
                }}
              >
                Renoncer
              </button>
            </div>

            <div className="aide">
              {saisie.quoi === 'pay'
                ? 'La référence rattache le règlement à sa pièce bancaire, pour la réconciliation.'
                : 'Une facture émise ne s’efface pas : elle serait un trou dans la numérotation. Un avoir portant son propre numéro la neutralise, et le motif y figure.'}
            </div>
          </Carte>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <Carte
          titre={`Factures de l’exercice ${exercice}`}
          action={
            <button
              type="button"
              className="secondaire"
              disabled={occupe}
              onClick={() =>
                agir(async () => {
                  await telecharger(
                    `/admin/invoices/export?year=${exercice}`,
                    `journal-ventes-${exercice}.csv`,
                  );
                  setSucces(`Journal des ventes ${exercice} téléchargé.`);
                })
              }
            >
              Export comptable
            </button>
          }
          sansCorps
        >
          {factures.length === 0 ? (
            <EtatVide titre="Aucune facture émise sur cet exercice." />
          ) : (
            <div className="tableau-conteneur">
              <table style={{ minWidth: 900 }}>
                <thead>
                  <tr>
                    <th>Numéro</th>
                    <th>Client</th>
                    <th>État</th>
                    <th className="droite">Hors taxes</th>
                    <th className="droite">TVA</th>
                    <th className="droite">TTC</th>
                    <th>Échéance</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {factures.map((f) => (
                    <tr key={f.id}>
                      <td className="mono">
                        {f.number}
                        {f.kind === 'credit_note' && (
                          <span style={{ marginLeft: 6 }}>
                            <PuceLibre ton="neutre">avoir</PuceLibre>
                          </span>
                        )}
                      </td>
                      <td>{f.account_name}</td>
                      <td>
                        <PuceLibre ton={ASPECT[f.status].ton}>{ASPECT[f.status].libelle}</PuceLibre>
                      </td>
                      <td className="nombre">{formaterFcfa(f.total_ht_xaf)}</td>
                      <td className="nombre">{formaterFcfa(f.total_vat_xaf)}</td>
                      <td className="nombre" style={{ fontWeight: 500 }}>
                        {formaterFcfa(f.total_ttc_xaf)}
                      </td>
                      <td style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                        {f.due_at ? formaterDateCivile(f.due_at) : '—'}
                      </td>
                      <td className="droite">
                        <div className="rangee-boutons" style={{ justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            className="secondaire"
                            onClick={() => agir(async () => setOuverte(await api.facture(f.id)))}
                          >
                            Voir
                          </button>
                          {/* Un avoir ne se règle ni ne s'annule : il neutralise déjà
                              une facture. L'annuler créerait un avoir d'avoir. */}
                          {f.status === 'issued' && f.kind === 'invoice' && (
                            <>
                              <button
                                type="button"
                                className="secondaire"
                                disabled={occupe}
                                onClick={() =>
                                  setSaisie({ id: f.id, numero: f.number ?? '', quoi: 'pay' })
                                }
                              >
                                Marquer réglée
                              </button>
                              <button
                                type="button"
                                className="secondaire"
                                disabled={occupe}
                                onClick={() =>
                                  setSaisie({ id: f.id, numero: f.number ?? '', quoi: 'cancel' })
                                }
                              >
                                Annuler
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="corps" style={{ paddingTop: 12 }}>
            <div className="aide" style={{ marginTop: 0 }}>
              L’export produit le journal des ventes SYSCOHADA : compte 411 clients au débit, 706
              services vendus et 4431 TVA facturée au crédit. Il refuse de sortir si le journal
              n’est pas équilibré.
            </div>
          </div>
        </Carte>
      </div>

      {ouverte && (
        <FactureAdmin
          facture={ouverte}
          occupe={occupe}
          onFermer={() => setOuverte(null)}
          onEmettre={() =>
            agir(async () => {
              const emise = await api.admin.emettreFacture(ouverte.id);
              setOuverte(emise);
              setSucces(`Facture ${emise.number} émise.`);
            })
          }
        />
      )}
    </>
  );
}

/**
 * Le document, plus le bouton d'émission quand il s'agit d'un brouillon.
 * L'émission consomme un numéro : elle mérite d'être faite devant la facture,
 * pas depuis une ligne de tableau.
 */
function FactureAdmin({
  facture,
  occupe,
  onFermer,
  onEmettre,
}: {
  facture: FactureDetaillee;
  occupe: boolean;
  onFermer: () => void;
  onEmettre: () => void;
}) {
  return (
    <>
      <FactureImprimable facture={facture} onFermer={onFermer} />
      {facture.status === 'draft' && (
        <div className="barre-emission sans-impression">
          <span>
            Brouillon — aucun numéro consommé. L’émission le fige : montants, mentions et période ne
            pourront plus changer.
          </span>
          <button type="button" className="accent" disabled={occupe} onClick={onEmettre}>
            Émettre la facture
          </button>
        </div>
      )}
    </>
  );
}

// --- Conformité — loi 025/2023 ----------------------------------------------

const LIBELLES_AUDIT: Record<string, string> = {
  'compliance.opt_outs.reveal': 'Registre des retraits consulté en clair',
  'compliance.opt_outs.export': 'Registre des retraits exporté',
  'compliance.consents.export': 'Registre des consentements exporté',
  'compliance.opt_out.manual': 'Retrait manuel',
  'compliance.retention.purge': 'Purge à 24 mois exécutée',
  'invoice.issue': 'Facture émise',
  'invoice.cancel': 'Facture annulée',
  'invoice.pay': 'Facture réglée',
  'invoice.export': 'Journal des ventes exporté',
  'account.topup': 'Compte crédité',
  'account.threshold': 'Seuil d’alerte modifié',
  'account.pricing': 'Tarif d’un compte fixé',
  'account.pricing.remove': 'Palier tarifaire retiré',
  'sender_id.decision': 'Décision sur un expéditeur',
  'sender_id.delete': 'Expéditeur supprimé',
  'message.marketing_sans_mention': 'Envoi publicitaire sans mention de retrait',
};

export function Conformite() {
  const [retention, setRetention] = useState<Retention | null>(null);
  const [audit, setAudit] = useState<EntreeAudit[] | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  /** Confirmation explicite : une purge réelle ne se déclenche pas d'un clic. */
  const [confirmationPurge, setConfirmationPurge] = useState(false);

  async function rafraichir() {
    const [r, a] = await Promise.all([api.admin.retention(), api.admin.audit(100)]);
    setRetention(r);
    setAudit(a.data);
  }

  useEffect(() => {
    rafraichir().catch(setErreur);
  }, []);

  async function agir(action: () => Promise<void>) {
    setOccupe(true);
    setErreur(null);
    setSucces(null);
    try {
      await action();
      await rafraichir();
    } catch (e) {
      setErreur(e);
    } finally {
      setOccupe(false);
    }
  }

  function lancerPurge(simulation: boolean) {
    return agir(async () => {
      const resultat = await api.admin.purger(simulation);
      const lignes = Object.entries(resultat.deleted_rows)
        .map(([table, n]) => `${n} ${table}`)
        .join(', ');
      setSucces(
        simulation
          ? `Simulation : ${resultat.dropped_partitions.length} partition(s) et ${lignes} seraient supprimées. Rien n’a été touché.`
          : `Purge exécutée : ${resultat.dropped_partitions.length} partition(s) supprimée(s), ${lignes}. Écritures et factures intactes.`,
      );
      setConfirmationPurge(false);
    });
  }

  if (erreur && !retention) return <MessageErreur erreur={erreur} />;
  if (!retention || !audit) return <Chargement quoi="Chargement de la conformité…" />;

  const totalLignes = Object.values(retention.rows).reduce((t, n) => t + n, 0);
  const aPurger = retention.partitions.length > 0 || totalLignes > 0;

  return (
    <>
      {erreur ? <MessageErreur erreur={erreur} /> : null}
      {succes ? <MessageSucces>{succes}</MessageSucces> : null}

      <div className="rangee" style={{ marginTop: succes || erreur ? 16 : 0 }}>
        <div className="large">
          <Carte titre="Rétention des journaux techniques">
            <div className="grille auto-210" style={{ marginBottom: 16 }}>
              <div className="carte">
                <div className="corps stat">
                  <div className="intitule">Durée de conservation</div>
                  <div className="valeur">{retention.retention_months} mois</div>
                  <div className="note">loi 025/2023</div>
                </div>
              </div>
              <div className="carte">
                <div className="corps stat">
                  <div className="intitule">Limite actuelle</div>
                  <div className="valeur" style={{ fontSize: 20 }}>
                    {formaterDateCivile(retention.cutoff)}
                  </div>
                  <div className="note">tout ce qui précède est purgeable</div>
                </div>
              </div>
              <div className="carte">
                <div className="corps stat">
                  <div className="intitule">Partitions expirées</div>
                  <div className="valeur">{retention.partitions.length}</div>
                  <div className="note">supprimées par détachement puis DROP</div>
                </div>
              </div>
              <div className="carte">
                <div className="corps stat">
                  <div className="intitule">Lignes expirées</div>
                  <div className="valeur">{formaterNombre(totalLignes)}</div>
                  <div className="note">événements, webhooks, audit</div>
                </div>
              </div>
            </div>

            {retention.partitions.length > 0 && (
              <div className="tableau-conteneur" style={{ marginBottom: 12 }}>
                <table style={{ minWidth: 520 }}>
                  <thead>
                    <tr>
                      <th>Partition</th>
                      <th>Table</th>
                      <th className="droite">Lignes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {retention.partitions.map((p) => (
                      <tr key={p.nom}>
                        <td className="mono">{p.nom}</td>
                        <td style={{ color: 'var(--muet)' }}>{p.parent}</td>
                        <td className="nombre">{formaterNombre(p.lignes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <Bandeau ton="vert" titre="Jamais purgé. ">
              {retention.preserved_ten_years.join(', ')} — conservation OHADA de dix ans. La purge à
              24 mois ne concerne que les journaux techniques ; les deux durées coexistent, et la
              base refuse elle-même de purger une pièce comptable.
            </Bandeau>

            <div className="rangee-boutons" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="secondaire"
                disabled={occupe}
                onClick={() => lancerPurge(true)}
              >
                Simuler la purge
              </button>
              {confirmationPurge ? (
                <>
                  <button
                    type="button"
                    className="accent"
                    disabled={occupe}
                    onClick={() => lancerPurge(false)}
                  >
                    Confirmer la suppression définitive
                  </button>
                  <button
                    type="button"
                    className="secondaire"
                    onClick={() => setConfirmationPurge(false)}
                  >
                    Renoncer
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="secondaire"
                  disabled={occupe || !aPurger}
                  onClick={() => setConfirmationPurge(true)}
                >
                  Purger maintenant
                </button>
              )}
            </div>

            <div className="aide">
              La purge tourne automatiquement chaque nuit à 4 h. Le bouton sert au rattrapage et à
              la démonstration devant un contrôleur. Simulez d’abord : la simulation compte ce qui
              partirait, sans rien toucher.
            </div>
          </Carte>
        </div>

        <div className="etroit">
          <Carte titre="Exports pour contrôle">
            <p style={{ fontSize: 13.5, color: 'var(--muet)', marginTop: 0 }}>
              Numéros en clair : c’est l’objet même de ces registres. Chaque extraction est
              journalisée avec son auteur.
            </p>
            <div className="rangee-boutons" style={{ flexDirection: 'column', gap: 10 }}>
              <button
                type="button"
                className="secondaire"
                disabled={occupe}
                onClick={() =>
                  agir(async () => {
                    await telecharger('/admin/compliance/opt-outs.csv', 'registre-retraits.csv');
                    setSucces('Registre des retraits exporté. L’accès est journalisé.');
                  })
                }
              >
                Registre des retraits
              </button>
              <button
                type="button"
                className="secondaire"
                disabled={occupe}
                onClick={() =>
                  agir(async () => {
                    await telecharger(
                      '/admin/compliance/consents.csv',
                      'registre-consentements.csv',
                    );
                    setSucces('Registre des consentements exporté. L’accès est journalisé.');
                  })
                }
              >
                Registre des consentements
              </button>
            </div>
          </Carte>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <Carte titre="Dernières purges" sansCorps>
          {retention.runs.length === 0 ? (
            <EtatVide titre="Aucune purge encore exécutée.">
              <p>
                La première aura lieu cette nuit, ou dès maintenant si vous la déclenchez ci-dessus.
              </p>
            </EtatVide>
          ) : (
            <div className="tableau-conteneur">
              <table style={{ minWidth: 820 }}>
                <thead>
                  <tr>
                    <th>Exécutée le</th>
                    <th>Déclenchement</th>
                    <th>État</th>
                    <th>Limite</th>
                    <th className="droite">Partitions</th>
                    <th>Lignes supprimées</th>
                  </tr>
                </thead>
                <tbody>
                  {retention.runs.map((r) => (
                    <tr key={r.id}>
                      <td>{formaterDate(r.started_at)}</td>
                      <td style={{ color: 'var(--muet)' }}>
                        {r.trigger}
                        {r.triggered_by && r.triggered_by !== 'system' ? ' · manuelle' : ''}
                      </td>
                      <td>
                        {r.status === 'succeeded' ? (
                          <PuceLibre ton="succes">réussie</PuceLibre>
                        ) : r.status === 'failed' ? (
                          <PuceLibre ton="echec">échouée</PuceLibre>
                        ) : (
                          <PuceLibre ton="attente">en cours</PuceLibre>
                        )}
                      </td>
                      <td style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                        {formaterDateCivile(r.cutoff)}
                      </td>
                      <td className="nombre">{r.dropped_partitions.length}</td>
                      <td style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                        {Object.entries(r.deleted_rows)
                          .map(([table, n]) => `${table} : ${n}`)
                          .join(' · ') || '—'}
                        {r.error && <span style={{ color: 'var(--rouge)' }}> {r.error}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
      </div>

      <div style={{ marginTop: 16 }}>
        <Carte titre="Journal d’audit" sansCorps>
          {audit.length === 0 ? (
            <EtatVide titre="Aucune action sensible encore enregistrée." />
          ) : (
            <div className="tableau-conteneur">
              <table style={{ minWidth: 900 }}>
                <thead>
                  <tr>
                    <th>Quand</th>
                    <th>Action</th>
                    <th>Auteur</th>
                    <th>Compte</th>
                    <th>Détail</th>
                    <th>Adresse</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((e) => (
                    <tr key={e.id}>
                      <td style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                        {formaterDate(e.created_at)}
                      </td>
                      <td>{LIBELLES_AUDIT[e.action] ?? e.action}</td>
                      <td style={{ color: 'var(--muet)' }}>{e.actor ?? e.actor_type}</td>
                      <td style={{ color: 'var(--muet)' }}>{e.account_name ?? '—'}</td>
                      <td
                        className="mono"
                        style={{ fontSize: 12, color: 'var(--muet)', maxWidth: 320 }}
                      >
                        {e.metadata ? JSON.stringify(e.metadata) : '—'}
                      </td>
                      <td style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                        {e.ip_address ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="corps" style={{ paddingTop: 12 }}>
            <div className="aide" style={{ marginTop: 0 }}>
              Le journal est lui-même soumis aux 24 mois. Les numéros y figurent masqués : un audit
              ne doit pas devenir une seconde copie du carnet d’adresses.
            </div>
          </div>
        </Carte>
      </div>
    </>
  );
}

// --- Ouverture d'un compte client -------------------------------------------

/**
 * Le pendant, côté exploitant, de l'inscription en libre-service de la
 * plateforme de test : mêmes champs, plus les mentions légales.
 *
 * Le NIF et le RCCM ne sont pas exigés ici, mais ils le seront à la première
 * facture — une facture qui en manque n'est pas opposable. Les demander au
 * moment d'ouvrir le compte évite de courir après le client le jour de la
 * facturation.
 */
function FormulaireOuvertureCompte({ onOuvert }: { onOuvert: (message: string) => void }) {
  const [champs, setChamps] = useState({
    company_name: '',
    legal_name: '',
    nif: '',
    rccm: '',
    address: '',
    email: '',
    password: '',
    full_name: '',
    sender_id: '',
  });
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);

  const modifier = (nom: keyof typeof champs) => (valeur: string) =>
    setChamps((c) => ({ ...c, [nom]: valeur }));

  async function soumettre() {
    setEnvoi(true);
    setErreur(null);
    try {
      const { account, user } = await api.admin.ouvrirCompte({
        company_name: champs.company_name,
        legal_name: champs.legal_name || undefined,
        nif: champs.nif || undefined,
        rccm: champs.rccm || undefined,
        address: champs.address || undefined,
        email: champs.email,
        password: champs.password,
        full_name: champs.full_name || undefined,
        sender_id: champs.sender_id || undefined,
      });
      onOuvert(
        `« ${account.name} » est ouvert, avec ${user.email} comme propriétaire. ` +
          'Transmettez-lui ses identifiants par un canal distinct de la console : ' +
          'c’est lui qui créera ensuite son équipe.',
      );
    } catch (e) {
      setErreur(e);
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
      {erreur ? <MessageErreur erreur={erreur} /> : null}

      <Grille>
        <Champ
          libelle="Raison sociale"
          valeur={champs.company_name}
          onChange={modifier('company_name')}
          requis
        />
        <Champ
          libelle="Dénomination légale"
          valeur={champs.legal_name}
          onChange={modifier('legal_name')}
        />
        <Champ libelle="NIF" valeur={champs.nif} onChange={modifier('nif')} />
        <Champ libelle="RCCM" valeur={champs.rccm} onChange={modifier('rccm')} />
        <Champ libelle="Adresse" valeur={champs.address} onChange={modifier('address')} />
        <Champ
          libelle="Expéditeur souhaité"
          valeur={champs.sender_id}
          onChange={modifier('sender_id')}
          aide="11 caractères maximum. Il restera en attente de validation opérateur."
        />
      </Grille>

      <div className="aide" style={{ marginTop: 4 }}>
        Premier utilisateur du compte — il en sera le <strong>propriétaire</strong>, et le seul à
        pouvoir en composer l’équipe.
      </div>

      <Grille>
        <Champ
          libelle="Nom du contact"
          valeur={champs.full_name}
          onChange={modifier('full_name')}
        />
        <Champ
          libelle="Adresse e-mail"
          type="email"
          valeur={champs.email}
          onChange={modifier('email')}
          requis
        />
        <Champ
          libelle="Mot de passe provisoire"
          type="password"
          valeur={champs.password}
          onChange={modifier('password')}
          aide="10 caractères minimum. À transmettre par un canal distinct de la console."
          requis
        />
      </Grille>

      <div>
        <button
          onClick={soumettre}
          disabled={envoi || !champs.company_name || !champs.email || champs.password.length < 10}
        >
          {envoi ? 'Ouverture…' : 'Ouvrir le compte'}
        </button>
      </div>
    </div>
  );
}
